import {AccountInfo, AuthenticationResult} from '@azure/msal-browser';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import React from 'react';
import {Keyword, TableConfiguration, VexCluster, UserRole} from 'types';
import {useMyFavoritesIdsOnlyQuery} from './hooks/myFavorites.generated';
import {useGetMyVexClustersQuery} from './hooks/myVexClusters.generated';
import {useMyTableConfigurationsLazyQuery} from './hooks/myTableConfigurations.generated';
import {useGetMyKeywordsQuery} from './hooks/myKeywords.generated';
import {computeRoleFlags, RoleFlags} from './roleFlags';
import {
	pruneStaleActiveRoles,
	resolveActiveRoles,
	writeStoredActiveRoles,
} from './activeRole';

export type UserContextType = AccountInfo & RoleFlags & {
	encodedUserId: string;
	roles: UserRole[];
	eligibleRoles: UserRole[];
	activeRoles: UserRole[];
	setActiveRoles: (roles: UserRole[]) => void;
	myVexClusters: VexCluster[];
	myKeywords: Keyword[];
	favoriteIds: {[key: string]: boolean} | undefined;
	tableConfigurations: TableConfiguration[];
	loadTableConfigurations: () => void;
};

export const UserContext = React.createContext<UserContextType>({} as any);

export const UserContextProvider: React.FC<{
	authResult: AuthenticationResult | null;
	children: React.ReactNode;
}> = ({authResult, children}) => {
	let account = {} as AccountInfo;
	let eligibleRoles = [] as UserRole[];
	let encodedUserId = '';
	let localAccountId = '';

	if (authResult && authResult.account) {
		const decoded = jwtDecode<JwtPayload & {[key: string]: any}>(
			authResult.accessToken,
		);
		account = authResult.account;
		eligibleRoles = decoded.roles || [];
		encodedUserId = encodeUserId(authResult.account.localAccountId);
		localAccountId = authResult.account.localAccountId;
	}

	// A user assigned several roles may choose to act as a subset of them.
	const activeRoles = resolveActiveRoles(localAccountId, eligibleRoles);
	const roles = activeRoles.length > 0 ? activeRoles : eligibleRoles;

	React.useEffect(() => {
		pruneStaleActiveRoles(localAccountId, eligibleRoles);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localAccountId, eligibleRoles.join(',')]);

	const setActiveRoles = (next: UserRole[]) => {
		const held = next.filter(role => eligibleRoles.includes(role));
		writeStoredActiveRoles(localAccountId, held);
		window.location.assign('/');
	};

	const roleFlags = computeRoleFlags(roles);

	const {data: vexClusterData} = useGetMyVexClustersQuery({skip: !authResult});
	const myVexClusters = React.useMemo(
		() => (vexClusterData?.myVexClusters || []) as VexCluster[],
		[vexClusterData],
	);

	const {data: keywordData} = useGetMyKeywordsQuery({skip: !authResult});
	const myKeywords = React.useMemo(
		() => (keywordData?.myKeywords ?? []) as Keyword[],
		[keywordData],
	);

	const {data: favoriteData, loading} = useMyFavoritesIdsOnlyQuery({
		skip: !authResult,
	});
	const favoriteIds = React.useMemo(() => {
		const favorites = favoriteData?.myFavorites;
		if (favorites && !loading) {
			const favIds = {} as {[key: string]: boolean};
			favorites.regulations.forEach(ref => {
				favIds[ref.id] = true;
			});
			favorites.regulatoryDocuments.forEach(ref => {
				favIds[ref.id] = true;
			});
			favorites.paragraphs.forEach(ref => {
				favIds[ref.id] = true;
			});
			favorites.requirements.forEach(ref => {
				favIds[ref.id] = true;
			});

			return favIds;
		}
	}, [favoriteData, loading]);

	const [loadTableConfigurationsQuery, {data: tableConfigurationData}] =
		useMyTableConfigurationsLazyQuery();
	const loadTableConfigurations = React.useCallback(() => {
		if (authResult) {
			loadTableConfigurationsQuery();
		}
	}, [authResult, loadTableConfigurationsQuery]);
	const tableConfigurations = React.useMemo(
		() =>
			(tableConfigurationData?.myTableConfigurations ??
				[]) as TableConfiguration[],
		[tableConfigurationData],
	);

	return (
		<UserContext.Provider
			value={{
				...account,
				...roleFlags,
				encodedUserId,
				roles,
				eligibleRoles,
				activeRoles,
				setActiveRoles,
				myVexClusters,
				myKeywords,
				favoriteIds,
				tableConfigurations,
				loadTableConfigurations,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};

function encodeUserId(uuid: string): string {
	const str = 'User:' + uuid;
	return window.btoa(str);
}

export const useUserContext = () => React.useContext(UserContext);
